import { createElement } from 'react';
import { captureException } from '@sentry/react';
import { BiDuplicate } from 'react-icons/bi';
import { TbArchive, TbArchiveOff, TbLock, TbLockOpen, TbStarFilled, TbStarOff } from 'react-icons/tb';
import { StateCreator } from 'zustand';

import { queryClient } from '@/clients';
import {
    fetchPrefetchedDetailQuery,
    getPrefetchedDetailQueryData,
    workspaceResourceDetailQueryOptions,
} from '@/queries/tanstack/prefetch';
import { CreateDashboardConfig, dashboardService } from '@/services';
import { toast } from '@/shared/components/toasts/PlatformToast';
import {
    appendEntity,
    createSingleFlightKey,
    mergeEntity,
    mergeEntityById,
    removeEntityById,
    replaceEntityById,
    replaceOptimisticEntityById,
    runSingleFlight,
    upsertEntityById,
} from '@/stores/helpers/collections';
import { reduceEntityDelta } from '@/stores/helpers/delta';
import { Dashboard } from '@/types';

import { DashboardActions, DashboardState } from '../types';

export const createDashboardActions: StateCreator<
    DashboardState & DashboardActions,
    [['zustand/immer', never]],
    [],
    DashboardActions
> = (set, get, store) => ({
    // Core
    fetchDashboards: async (params) => {
        set({ loading: true });

        try {
            const dashboards = await dashboardService.list(params);

            set({ dashboards, loading: false, error: undefined });
        } catch (error) {
            captureException(error);

            set({ loading: false, error: 'Failed to load dashboards' });
        }
    },
    fetchDashboardById: async (id) => {
        const queryOptions = workspaceResourceDetailQueryOptions.dashboards(id);
        const cachedDashboard = getPrefetchedDetailQueryData(queryClient, queryOptions);

        set({
            loadingDashboard: !cachedDashboard,
            error: undefined,
            ...(cachedDashboard ? { selectedDashboard: cachedDashboard } : {}),
        });

        try {
            const dashboard = await fetchPrefetchedDetailQuery(queryClient, queryOptions);

            set({ selectedDashboard: dashboard, loadingDashboard: false });
        } catch (error) {
            captureException(error);

            set((state) => {
                state.error = 'Failed to fetch dashboard detail';
                state.loadingDashboard = false;
            });
        }
    },
    clearSelectedDashboard: () => {
        set((state) => {
            state.selectedDashboard = null;
        });
    },
    createDashboard: async (input, config?: CreateDashboardConfig) =>
        runSingleFlight(createSingleFlightKey('dashboards:create', { config, input }), async () => {
            const tempId = `temp-${Date.now()}`;
            const optimistic = { id: tempId, ...input } as Dashboard;

            set({ dashboards: appendEntity(get().dashboards, optimistic) });

            try {
                const dashboard = await dashboardService.create(input, config);

                set({ dashboards: replaceOptimisticEntityById(get().dashboards, tempId, dashboard) });

                return dashboard;
            } catch (error) {
                captureException(error);

                set({ dashboards: removeEntityById(get().dashboards, tempId), error: 'Failed to create dashboard' });
            }
        }),
    updateDashboard: async (id, updates) => {
        const prev =
            get().dashboards.find((f) => f.id === id) ??
            (get().selectedDashboard?.id === id ? get().selectedDashboard : undefined);

        if (!prev) {
            return;
        }

        set({
            dashboards: mergeEntityById(get().dashboards, id, updates),
            selectedDashboard:
                get().selectedDashboard?.id === id
                    ? mergeEntity(get().selectedDashboard!, updates)
                    : get().selectedDashboard,
        });

        try {
            const dashboard = await dashboardService.update(id, updates);

            set({
                dashboards: mergeEntityById(get().dashboards, id, dashboard),
                selectedDashboard:
                    get().selectedDashboard?.id === id
                        ? mergeEntity(get().selectedDashboard!, dashboard)
                        : get().selectedDashboard,
            });
        } catch (error) {
            captureException(error);

            set({
                dashboards: replaceEntityById(get().dashboards, id, prev),
                selectedDashboard: get().selectedDashboard?.id === id ? prev : get().selectedDashboard,
                error: 'Failed to update dashboard',
            });
        }
    },
    deleteDashboard: async (id) => {
        const prev = get().dashboards;

        set({
            dashboards: removeEntityById(get().dashboards, id),
            selectedDashboard: get().selectedDashboard?.id === id ? null : get().selectedDashboard,
        });

        try {
            await dashboardService.delete(id);
        } catch (error) {
            captureException(error);

            set({ dashboards: prev, error: 'Failed to delete dashboard' });
        }
    },
    applyDashboardDelta: (packet) => {
        if (packet.entity !== 'dashboard') {
            return;
        }

        const next = reduceEntityDelta<Dashboard>(get().dashboards, get().selectedDashboard, packet);

        if (!next) {
            return;
        }

        set({
            dashboards: next.items,
            selectedDashboard: next.selected,
        });
    },
    // - Aliases
    duplicateDashboard: async (id) => {
        const prev = get().dashboards;

        if (!prev) {
            return;
        }

        try {
            const duplicated = await dashboardService.duplicate(id);

            set({ dashboards: upsertEntityById(get().dashboards, duplicated, { prepend: true }) });

            toast(`Dashboard Duplicated`, {
                icon: createElement(BiDuplicate, { size: '1rem' }),
                description: duplicated.name,
                resource: { id: duplicated.id, type: 'dashboard' },
            });

            return duplicated;
        } catch (error) {
            captureException(error);

            set({ dashboards: prev, error: 'Failed to duplicate dashboard' });

            toast.error('Failed to duplicate dashboard');
        }
    },
    toggleDashboardFavorite: async (id, favorited) => {
        const prev = get().dashboards.find((f) => f.id === id);

        if (!prev) {
            return;
        }

        set((state) => {
            const dashboard = state.dashboards.find((f) => f.id === id);
            if (dashboard) {
                dashboard.favorited = favorited;
            }

            // Also update selectedDashboard if it matches
            if (state.selectedDashboard?.id === id) {
                state.selectedDashboard.favorited = favorited;
            }
        });

        try {
            await dashboardService.setFavorite(id, favorited);

            toast(`${favorited ? 'Added to' : 'Removed from'} Favorites`, {
                icon: createElement(favorited ? TbStarFilled : TbStarOff, { size: '1rem' }),
                description: prev.name,
                resource: { id, type: 'dashboard' },
            });
        } catch (error) {
            captureException(error);

            set((state) => {
                const dashboard = state.dashboards.find((f) => f.id === id);
                if (dashboard) {
                    dashboard.favorited = prev.favorited;
                }

                // Revert selectedDashboard favorited if it matches
                if (state.selectedDashboard?.id === id) {
                    state.selectedDashboard.favorited = prev.favorited;
                }

                state.error = 'Failed to update favorite';
            });
        }
    },
    toggleDashboardLock: async (id, locked) => {
        const prev = get().dashboards.find((f) => f.id === id);

        if (!prev) {
            return;
        }

        set((state) => {
            const dashboard = state.dashboards.find((f) => f.id === id);
            if (dashboard) {
                dashboard.locked = locked;
            }

            // Also update selectedDashboard if it matches
            if (state.selectedDashboard?.id === id) {
                state.selectedDashboard.locked = locked;
            }
        });

        try {
            await dashboardService.setLocked(id, locked);

            toast(`Dashboard ${locked ? 'Locked' : 'Unlocked'}`, {
                icon: createElement(locked ? TbLock : TbLockOpen, { size: '1rem' }),
                description: prev.name,
                resource: { id, type: 'dashboard' },
            });
        } catch (error) {
            captureException(error);

            set((state) => {
                const dashboard = state.dashboards.find((f) => f.id === id);
                if (dashboard) {
                    dashboard.locked = prev.locked;
                }

                // Revert selectedDashboard locked if it matches
                if (state.selectedDashboard?.id === id) {
                    state.selectedDashboard.locked = prev.locked;
                }

                state.error = 'Failed to update favorite';
            });
        }
    },
    toggleDashboardArchive: async (id, archived) => {
        const prev = get().dashboards.find((f) => f.id === id);

        if (!prev) {
            return;
        }

        set((state) => {
            const dashboard = state.dashboards.find((f) => f.id === id);
            if (dashboard) {
                dashboard.archived = archived;
            }

            // Also update selectedDashboard if it matches
            if (state.selectedDashboard?.id === id) {
                state.selectedDashboard.archived = archived;
            }
        });

        try {
            await dashboardService.setArchived(id, archived);

            toast(`Dashboard ${archived ? 'Archived' : 'Unarchived'}`, {
                icon: createElement(archived ? TbArchive : TbArchiveOff, { size: '1rem' }),
                description: prev.name,
                resource: { id, type: 'dashboard' },
            });
        } catch (error) {
            captureException(error);

            set((state) => {
                const dashboard = state.dashboards.find((f) => f.id === id);
                if (dashboard) {
                    dashboard.archived = prev.archived;
                }

                // Revert selectedDashboard archived if it matches
                if (state.selectedDashboard?.id === id) {
                    state.selectedDashboard.archived = prev.archived;
                }

                state.error = 'Failed to update favorite';
            });
        }
    },
});
